import type { AppState, ScenarioMark } from '../core/types';
import { deriveDisplaySet } from '../domain/displaySet';
import { totalPolygonArea } from '../domain/measurement';
import { recordsForSelection } from '../domain/selectionSet';
import type { LabelConfig, LegendConfig } from './config';
import type { PresentationWorkspaceConfig } from './workspace';
import { buildLegendComposition, type LegendComposition } from './legend';
import { drawnScenario } from './scenarioActions';

export type CompositionDestination = 'screen' | 'clipboard' | 'png' | 'pdf';

export interface CompositionMetric {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly unit: '' | 'ha';
}

export interface PresentationComposition {
  readonly title: string;
  readonly subtitle: string;
  readonly date: string;
  readonly backgroundColor: string;
  readonly visibleLayerId: string | null;
  readonly visibleFeatureIds: readonly string[];
  readonly legend: LegendConfig;
  readonly legendModel: LegendComposition | null;
  readonly labels: LabelConfig;
  readonly workspace: PresentationWorkspaceConfig;
  readonly visibleLayerFeatureIds: Readonly<Record<string, readonly string[]>>;
  readonly metrics: readonly CompositionMetric[];
  /** V1.9.0: name of the proposal drawn on the map (null = «Actual»). */
  readonly scenario: string | null;
  /** Where its single mark goes (see scenarioMarkPlacement). */
  readonly scenarioMark?: ScenarioMark;
}

export function selectionCompositionSummary(state: AppState): { readonly count: number; readonly areaHa: number } {
  const layer = state.layers.find(({ id }) => id === state.selectionSet.layerId);
  const records = layer ? recordsForSelection(layer, state.selectionSet) : [];
  const geometries = records.flatMap(({ feature }) => { const geometry = feature.getGeometry(); return geometry?.getType().includes('Polygon') ? [geometry] : []; });
  return { count: state.selectionSet.entries.length, areaHa: geometries.length ? totalPolygonArea(geometries) / 10_000 : 0 };
}

/** Single serializable input for screen, future clipboard image, PNG and PDF renderers. */
export function buildPresentationComposition(state: AppState): PresentationComposition {
  const config = state.presentationWorkspace;
  const display = deriveDisplaySet(state);
  const layer = state.layers.find(({ id }) => id === state.activeLayerId) ?? state.layers.find(({ kind }) => kind === 'master') ?? null;
  const visible = layer ? display.layerFeatureIds.get(layer.id) ?? new Set(layer.features.map(({ id }) => id)) : new Set<string>();
  const visibleRecords = layer?.features.filter(({ id }) => visible.has(id)) ?? [];
  const visibleGeometries = visibleRecords.flatMap(({ feature }) => { const geometry = feature.getGeometry(); return geometry?.getType().includes('Polygon') ? [geometry] : []; });
  const selected = selectionCompositionSummary(state);
  const values = { entities: visibleRecords.length, 'area-ha': visibleGeometries.length ? totalPolygonArea(visibleGeometries) / 10_000 : 0, 'selected-entities': selected.count, 'selected-area-ha': selected.areaHa };
  const visibleLayerFeatureIds = Object.fromEntries(state.layers.filter(({ visible: enabled }) => enabled).map(({ id }) => [id, [...(display.layerFeatureIds.get(id) ?? [])]]));
  return {
    title: config.title,
    subtitle: config.subtitle,
    date: config.date,
    backgroundColor: config.backgroundColor,
    visibleLayerId: layer?.id ?? null,
    visibleFeatureIds: [...visible],
    legend: state.classificationConfig.legend,
    legendModel: buildLegendComposition(state),
    labels: state.labelConfig,
    workspace: config,
    visibleLayerFeatureIds,
    metrics: config.kpis.filter(({ visible: enabled }) => enabled).map((item) => ({ id: item.id, label: item.label, value: values[item.metric], unit: item.metric.includes('area') ? 'ha' : '' })),
    scenario: drawnScenario(state)?.name ?? null,
    ...(state.scenarios.mark ? { scenarioMark: state.scenarios.mark } : {}),
  };
}
